import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Linkedin, Music2, Twitter, Mail, Phone, MessageCircle } from "lucide-react";

import { useSite } from "@/contexts/SiteContext";

const COL = [
  {
    heading: "Company",
    links: [
      { label: "Our Company", to: "/our-company" },
      { label: "Our Advisory Team", to: "/our-advisory-team" },
      { label: "Why PIRAC", to: "/why-pirac" },
      { label: "Framework", to: "/framework" },
    ],
  },
  {
    heading: "Success Stories",
    links: [
      { label: "Case Studies", to: "/case-studies" },
      { label: "Testimonials", to: "/testimonials" },
      { label: "Awards", to: "/awards" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Property Valuation", to: "/property-valuation" },
      { label: "Mortgage Calculator", to: "/calculators/mortgage" },
      { label: "Sales Proceeds Calculator", to: "/calculators/sales-proceeds" },
      { label: "Purchase Calculator", to: "/calculators/purchase" },
      { label: "Blogs & Insights", to: "/blogs" },
    ],
  },
];

export default function Footer() {
  // Contact details come from Site Settings so they can be corrected without a
  // deployment; the context supplies the previous hardcoded values as fallbacks
  // while the request is in flight.
  const { site } = useSite();
  const whatsapp = (site.whatsappNumber || "").replace(/\D/g, "");

  return (
    <footer className="relative bg-[#0c1728] border-t border-[#1a2940] pt-20 pb-10" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img
                src="/assets/pirac-logo.webp"
                alt="PIRAC"
                className="h-14 w-14 object-cover"
                style={{ clipPath: "inset(6% 6% 6% 6%)" }}
              />
              <div>
                <div className="font-serif text-2xl text-[#f7f5f0] tracking-wider">PIRAC</div>
              </div>
            </div>
            <p className="mt-6 text-sm text-[#9ca3af] leading-relaxed max-w-sm">
              Property Investment Research and Advisory Centre. Independent, research-driven property advice for Singapore property owners, investors, business owners and institutions.
            </p>
            <div className="mt-8 space-y-2 text-sm">
              {site.contactEmail && (
                <a href={`mailto:${site.contactEmail}`} className="min-h-11 py-1 -my-1 flex items-center gap-3 text-[#d8d8d8] hover:text-[#cca24d] transition-colors break-all" data-testid="footer-email">
                  <Mail className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} /> {site.contactEmail}
                </a>
              )}
              {site.contactPhone && (
                <a href={`tel:${site.contactPhone.replace(/\s/g, "")}`} className="min-h-11 py-1 -my-1 flex items-center gap-3 text-[#d8d8d8] hover:text-[#cca24d] transition-colors" data-testid="footer-phone">
                  <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {site.contactPhone}
                </a>
              )}
              {whatsapp && (
                <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer" className="min-h-11 py-1 -my-1 flex items-center gap-3 text-[#d8d8d8] hover:text-[#cca24d] transition-colors" data-testid="footer-whatsapp">
                  <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> WhatsApp PIRAC
                </a>
              )}
            </div>
          </div>

          {/* Nav cols */}
          {COL.map((c) => (
            <div key={c.heading} className="lg:col-span-2">
              <div className="overline mb-6 text-[#cca24d]/80">{c.heading}</div>
              <ul className="space-y-3">
                {c.links.map((l) => (
                  <li key={l.label}>
                    {l.external ? (
                      <a href={l.href} target="_blank" rel="noopener noreferrer" className="min-h-11 py-2 -my-1 inline-flex items-center text-sm text-[#d8d8d8] hover:text-[#cca24d] transition-colors editorial-link">
                        {l.label}
                      </a>
                    ) : (
                      <Link to={l.to} className="min-h-11 py-2 -my-1 inline-flex items-center text-sm text-[#d8d8d8] hover:text-[#cca24d] transition-colors editorial-link">
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact col */}
          <div className="lg:col-span-2">
            <div className="overline mb-6 text-[#cca24d]/80">Engage</div>
            <ul className="space-y-3">
              <li><Link to="/contact" className="min-h-11 py-2 -my-1 inline-flex items-center text-sm text-[#d8d8d8] hover:text-[#cca24d] transition-colors editorial-link">Book a Consultation</Link></li>
              <li><Link to="/privacy-policy" className="min-h-11 py-2 -my-1 inline-flex items-center text-sm text-[#d8d8d8] hover:text-[#cca24d] transition-colors editorial-link">Privacy Policy</Link></li>
              <li><Link to="/terms-of-use" className="min-h-11 py-2 -my-1 inline-flex items-center text-sm text-[#d8d8d8] hover:text-[#cca24d] transition-colors editorial-link">Terms of Use</Link></li>
              <li><Link to="/disclaimer" className="min-h-11 py-2 -my-1 inline-flex items-center text-sm text-[#d8d8d8] hover:text-[#cca24d] transition-colors editorial-link">Disclaimer</Link></li>
              <li><Link to="/admin/login" className="min-h-11 py-2 -my-1 inline-flex items-center text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors editorial-link" data-testid="footer-admin-login">Admin Login</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 gold-hairline" />

        <div className="mt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <p className="text-xs text-[#9ca3af] tracking-wide">
            © 2026 Property Investment Research and Advisory Centre. All rights reserved.
          </p>
          {/*
            Only render icons for accounts that are actually configured in Site
            Settings. These previously all pointed at "#", so every one of them
            was a visible link that did nothing when clicked.
          */}
          <div className="flex items-center gap-4">
            {/* Keys must match SOCIAL_KEYS in pages/admin/Settings.jsx. */}
            {[
              { Icon: Linkedin, label: "LinkedIn", key: "linkedin" },
              { Icon: Facebook, label: "Facebook", key: "facebook" },
              { Icon: Instagram, label: "Instagram", key: "instagram" },
              { Icon: Youtube, label: "YouTube", key: "youtube" },
              { Icon: Music2, label: "TikTok", key: "tiktok" },
              { Icon: Twitter, label: "X (Twitter)", key: "x" },
            ]
              .map((entry) => ({ ...entry, href: (site.socialLinks || {})[entry.key] }))
              .filter((entry) => Boolean(entry.href))
              .map(({ Icon, label, key, href }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="h-9 w-9 flex items-center justify-center border border-[#1a2940] text-[#9ca3af] hover:text-[#cca24d] hover:border-[#cca24d] transition-colors"
                  data-testid={`footer-social-${key}`}
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
              ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
